import {
  canStartAuthenticationRedirect,
  getAccessToken,
  refreshAccessToken,
  signIn,
  waitForAuthReady
} from "./oidc-auth-service";
import stringHasValue from "./string-has-value";
import {getImpersonatingEmployee} from "../app/impersonate-service";

const toHeaders = (headers) => {
  if (headers instanceof Headers) {
    return headers;
  }

  return new Headers(headers ?? {});
};

const buildFetchOptions = async ({fetchOptions:options, skipAuth = false}) => {
  const fetchOptions = options ? { ...options } : {};
  const headers = toHeaders(fetchOptions.headers);

  if (!skipAuth) {
    let accessToken = await getAccessToken();
    if (!stringHasValue(accessToken)) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        accessToken = await getAccessToken();
      }
    }

    if (stringHasValue(accessToken)) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    } else {
      headers.delete("Authorization");
    }
  }

  const impersonating = getImpersonatingEmployee();
  if (impersonating) {
    //note: it would be nice to get this from configuration
    //but it isn't critical, and I don't want to make every fetchwrapper and post wrapper call etc need
    //an additional parameter.  The configuration may be put in session storage if we really wanted to do this.
    headers.set("acting-as", impersonating.churchAccountId);
  } else {
    headers.delete("acting-as");
  }

  fetchOptions.headers = headers;
  return fetchOptions;
};

const parseSuccessfulResponse = async (response, url) => {
  if (!response.headers.get("content-type")?.includes("application/json")) {
    window.dispatchEvent(new Event("renew"));
    return response;
  }

  const json = await response.json().catch((e) => {
    //we get empty responses and those are acceptable, but the json parser will fail.
    //we don't want to log that as an error.
    const errorText = typeof e === "string" ? e : e?.message;
    if (stringHasValue(errorText) && errorText.includes("Unexpected end of JSON input") !== true) {
      console.log(
        `json parse on a success failed from ${url}.\n ${errorText}`
      );
    }
    return null;
  });

  window.dispatchEvent(new Event("renew"));
  return json;
};

const fetchWrapper = async ({ url, fetchOptions:options, skipAuth = false }) => {
  let response;
  try {
    if (!skipAuth) {
      const authReady = await waitForAuthReady();
      if (!authReady) {
        throw new Error(`Authentication is not ready for request: ${url}`);
      }
    }

    let fetchOptions = await buildFetchOptions({fetchOptions:options, skipAuth});
    response = await fetch(url, fetchOptions);

    const { status } = response;
    if (response.ok) {
      return await parseSuccessfulResponse(response, url);
    }

    if (status === 401 && !skipAuth) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        fetchOptions = await buildFetchOptions({fetchOptions:options, skipAuth});
        response = await fetch(url, fetchOptions);
        if (response.ok) {
          return await parseSuccessfulResponse(response, url);
        }
      }

      if (canStartAuthenticationRedirect()) {
        await signIn();
      }
    }

    throw new Error(`Response not OK, requesting:${url} returned status: ${response.status}`);
  } catch (err) {
    // 403 Forbidden = not authorized
    // Other errors that can not be handled by attempting relogin.
    //We want the fetch wrapper to throw this so that these failed responses
    //reflect as failures that the UI can handle rather than looking like a false success

    console.error(err); // eslint-disable-line no-console

    throw err;
  }
};

const postWrapper = async ({url, data}) => {

  const fetchOptions = {
    method:"POST",
    body: data ? JSON.stringify(data) : null,
    headers: {
      'Content-Type': 'application/json'
    },
  }

  return await fetchWrapper({url, fetchOptions})

}

const putWrapper = async ({url, data}) => {

  const fetchOptions = {
    method:"PUT",
    body: data ?  JSON.stringify(data) : null,
    headers: {
      'Content-Type': 'application/json'
    },
  }

  return await fetchWrapper({url, fetchOptions})

}

const deleteWrapper = async({url}) => {
  const fetchOptions = {
    method:"DELETE",
    headers: {
      'Content-Type': 'application/json'
    },
  }

  return await fetchWrapper({url, fetchOptions})
}

export {postWrapper,putWrapper, deleteWrapper}

export default fetchWrapper;
